import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {MutationObserver | null} */
  #gridObserver = null;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    // Widok mógł zostać odtworzony z sessionStorage (skrypt w snippets/product-grid.liquid), a kolejne strony
    // doładowuje infinite scroll – w obu przypadkach dopasowujemy sizes zdjęć do bieżącego widoku.
    this.#syncImageSizes();
    const { grid } = this.refs;
    if (grid) {
      this.#gridObserver = new MutationObserver(() => this.#syncImageSizes());
      this.#gridObserver.observe(grid, { childList: true });
    }
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#gridObserver?.disconnect();
  }

  /**
   * sizes zdjęć w kartach zakłada liczbę kolumn na mobile z ustawień sekcji (np. 50vw przy 2 kolumnach).
   * W widoku „mobile-single” karta ma pełną szerokość, więc ostatni człon sizes zamieniamy na 100vw;
   * po powrocie do innego widoku przywracamy oryginał.
   */
  #syncImageSizes() {
    const { grid } = this.refs;
    if (!grid) return;

    const singleColumn = grid.getAttribute('product-grid-view') === 'mobile-single';
    for (const image of grid.querySelectorAll('img[sizes]')) {
      if (!(image instanceof HTMLImageElement)) continue;
      image.dataset.sizesDefault ??= image.sizes;
      const sizes = singleColumn
        ? image.dataset.sizesDefault.replace(/,\s*[\d.]+vw\s*$/, ', 100vw')
        : image.dataset.sizesDefault;
      if (image.sizes !== sizes) image.sizes = sizes;
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
    this.#syncImageSizes();
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
